import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useFicha } from '@/features/fichas/hooks'

export default function FichaDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, isError, error } = useFicha(id)

  return (
    <div className="flex flex-col gap-4" data-testid="ficha-detail-page">
      <div>
        <Link
          to="/app/fichas"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Volver a fichas
        </Link>
      </div>

      {isLoading && (
        <Card>
          <CardContent>
            <div className="flex flex-col gap-2">
              <Skeleton className="h-6 w-64" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-24 w-full" />
            </div>
          </CardContent>
        </Card>
      )}

      {isError && (
        <div
          role="alert"
          className="rounded-md border border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5 p-3 text-sm text-[color:var(--color-fitlab-red-hover)]"
        >
          No se pudo cargar la ficha: {error?.message ?? 'error desconocido'}
        </div>
      )}

      {data && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Ficha {data.template}
              <Badge
                tone={
                  data.template === 'laboratorio'
                    ? 'danger'
                    : data.template === 'antropometria'
                      ? 'success'
                      : 'info'
                }
              >
                {data.template}
              </Badge>
            </CardTitle>
            <CardDescription>
              Trabajador: <strong>{data.worker_name ?? data.worker_id}</strong> — Operativo{' '}
              <strong>{data.operativo_id ?? data.encounter_id}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <section>
              <h3 className="mb-2 text-sm font-semibold text-slate-700">Mediciones</h3>
              <dl className="divide-y divide-slate-100 text-sm">
                {Object.entries(data.values ?? {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between py-1.5">
                    <dt className="text-slate-500">{k}</dt>
                    <dd className="font-medium text-slate-900">{String(v)}</dd>
                  </div>
                ))}
                {Object.keys(data.values ?? {}).length === 0 && (
                  <p className="py-2 text-slate-500">Sin mediciones registradas.</p>
                )}
              </dl>
            </section>
            {data.custom_fields && Object.keys(data.custom_fields).length > 0 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-700">Campos personalizados</h3>
                <dl className="divide-y divide-slate-100 text-sm">
                  {Object.entries(data.custom_fields).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between py-1.5">
                      <dt className="text-slate-500">{k}</dt>
                      <dd className="font-medium text-slate-900">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}
            <section className="text-xs text-slate-500 sm:col-span-2">
              <p>
                Creada el{' '}
                {new Date(data.created_at).toLocaleString('es-CL', {
                  timeZone: 'America/Santiago',
                })}{' '}
                por {data.created_by_name ?? data.created_by}
              </p>
            </section>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
